import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { CKEditor } from '~/components/Shared/CKEditor4/CKEditor4';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { FullPageOverlay } from '~/eds/src/components/FullPageOverlay/FullPageOverlay';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { editorConfig } from '~/features/playbooks/constants';
import { ImportVariation } from '~/features/playbooks/setup/import-variation/ImportVariation';
import { api } from '~/redux/api/createApi';
import {
  GuidelineClauseVariation,
  GuidelineWithId,
} from '~/redux/api/types/playbooks/guidelines';

import { Variation } from '../variation/Variation';

type Props = {
  guideline?: GuidelineWithId;
  onClose: () => void;
};

type Guideline = {
  name: string;
  guideline: string;
  sample: string;
  suggest_redlines?: boolean;
  variations?: GuidelineClauseVariation[];
};

const isGuidelineValid = (guideline: Guideline) => {
  return guideline.name.trim().length > 0 && guideline.guideline.length > 0;
};

const stripHtmlTags = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() ?? '';
};

const isGuidelineDirty = (
  original: Guideline | undefined,
  current: Guideline,
) => {
  if (!original) {
    return true;
  }
  return (
    JSON.stringify({
      name: original.name,
      guideline: original.guideline,
      sample: original.sample || [],
      suggest_redlines: original.suggest_redlines || false,
    }) !== JSON.stringify(current)
  );
};

export const Form = ({ guideline, onClose }: Props) => {
  const intl = useIntl();
  const { toast } = useToast();
  const [clausePanelVisible, setClausePanelVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<Guideline>({
    name: '',
    guideline: '',
    sample: '',
    suggest_redlines: true,
    variations: [],
  });

  const [guidelineCharCount, setGuidelineCharCount] = useState(0);
  const [sampleCharCount, setSampleCharCount] = useState(0);
  const [createGuideline, { isLoading: isCreatingGuideline }] =
    api.endpoints.createGuideline.useMutation();

  const [updateGuideline, { isLoading: isUpdatingGuideline }] =
    api.endpoints.updateGuideline.useMutation();

  const handleInserVariation = (variation: GuidelineClauseVariation) => {
    setState((prevState) => ({
      ...prevState,
      variations: [...(prevState.variations || []), variation],
    }));

    setClausePanelVisible(false);
    setTimeout(() => {
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }, 100);
  };

  useEffect(() => {
    if (guideline) {
      setState({
        name: guideline.name,
        guideline: guideline.guideline,
        sample: guideline.sample ?? '',
        suggest_redlines: guideline.suggest_redlines || false,
        variations: guideline.variations || [],
      });
      setGuidelineCharCount(stripHtmlTags(guideline.guideline).trim().length);
      setSampleCharCount(stripHtmlTags(guideline.sample ?? '').trim().length);
    } else {
      setState({
        name: '',
        guideline: '',
        sample: '',
        suggest_redlines: true,
        variations: [],
      });
    }
  }, [guideline]);

  return (
    <FullPageOverlay
      isVisible
      isFullPage
      onHide={onClose}
      actions={[
        {
          text: intl.formatMessage({
            id: 'guidelines.form.submitButton',
            defaultMessage: 'Submit',
            description: 'Button text to create a new guideline',
          }),
          level: 'primary',
          disabled:
            !isGuidelineValid(state) ||
            !isGuidelineDirty(guideline as Guideline | undefined, state),
          isLoading: isCreatingGuideline || isUpdatingGuideline,
          onClick: async () => {
            try {
              if (guideline?.uuid) {
                await updateGuideline({
                  uuid: guideline.uuid,
                  ...state,
                }).unwrap();
              } else {
                await createGuideline(state).unwrap();
              }
              onClose();
            } catch {
              toast({
                status: 'danger',
                title: intl.formatMessage({
                  id: 'guidelines.form.errorToastTitle',
                  defaultMessage: 'Error creating guideline',
                  description:
                    'Title for the error toast when creating a guideline fails',
                }),
                message: intl.formatMessage({
                  id: 'guidelines.form.errorToastMessage',
                  defaultMessage:
                    'There was an error creating the guideline. Please try again.',
                  description:
                    'Message for the error toast when creating a guideline fails',
                }),
              });
            }
          },
        },
      ]}
      title={
        guideline?.uuid
          ? intl.formatMessage({
              id: 'guidelines.form.editTitle',
              defaultMessage: 'Edit Guideline',
              description: 'Title for editing an existing guideline',
            })
          : intl.formatMessage({
              id: 'guidelines.form.setupModalTitle',
              defaultMessage: 'Create Guideline',
              description: 'Title for the setup playbook modal',
            })
      }
      cancelAction={{
        text: intl.formatMessage({
          id: 'guidelines.form.cancelButton',
          defaultMessage: 'Cancel',
          description: 'Button text to cancel creating a new guideline',
        }),
        level: 'tertiary',
        onClick: () => {
          onClose();
        },
      }}
    >
      <div ref={containerRef}>
        <Layout spacing={4}>
          <Box flex="5">
            <Panel
              title={intl.formatMessage({
                id: 'guidelines.form.panelTitle',
                defaultMessage: 'Guideline Details',
                description: 'Title for the guideline details panel',
              })}
            >
              <Layout
                preset="sections"
                style={{ margin: '0 auto', maxWidth: 800 }}
              >
                <FormField
                  input={TextInput}
                  label={intl.formatMessage({
                    id: 'guidelines.form.titleLabel',
                    defaultMessage: 'Guideline Name',
                    description: 'Label for the guideline name input field',
                  })}
                  name="name"
                  placeholder={intl.formatMessage({
                    id: 'guidelines.form.titlePlaceholder',
                    defaultMessage: 'Enter guideline name',
                    description:
                      'Placeholder for the guideline title input field',
                  })}
                  value={state.name}
                  onChange={(value) => {
                    setState((prevState: Guideline) => ({
                      ...prevState,
                      name: value as string,
                    }));
                  }}
                />
                <FormField
                  input={CKEditor}
                  inputProps={{
                    config: editorConfig,
                    data: guideline?.guideline,
                    key: guideline?.guideline,
                  }}
                  label={intl.formatMessage({
                    id: 'guidelines.form.guidelineLabel',
                    defaultMessage: 'Guideline',
                    description:
                      'Label for the guideline description input field',
                  })}
                  name="guideline"
                  placeholder={intl.formatMessage({
                    id: 'guidelines.form.guidellinePlaceholder',
                    defaultMessage: 'Enter guideline',
                    description:
                      'Placeholder for the guideline description input field',
                  })}
                  value={state.guideline}
                  footer={intl.formatMessage(
                    {
                      id: 'guidelines.form.characterCountFooter',
                      defaultMessage: '{current}/{limit} characters',
                      description:
                        'Footer text for the guideline description input field',
                    },
                    {
                      current: guidelineCharCount,
                      limit: 10000,
                    },
                  )}
                  onChange={(editor: any) => {
                    const value = editor.getData();
                    const plainText = editor.document
                      .getBody()
                      .getText()
                      .trim();

                    setGuidelineCharCount(plainText.length);
                    setState((prevState: Guideline) => ({
                      ...prevState,
                      guideline: value as string,
                    }));
                  }}
                />
                <FormField
                  input={CKEditor}
                  inputProps={{
                    config: editorConfig,
                    data: guideline?.sample,
                    key: guideline?.sample,
                  }}
                  label={intl.formatMessage({
                    id: 'guidelines.form.sampleWordingLabel',
                    defaultMessage: 'Sample Wording',
                    description:
                      'Label for the guideline description input field',
                  })}
                  name="sampleWording"
                  placeholder={intl.formatMessage({
                    id: 'guidelines.form.sampleWordingPlaceholder',
                    defaultMessage: 'Enter Sample Wording',
                    description:
                      'Placeholder for the guideline description input field',
                  })}
                  value={state.sample}
                  footer={intl.formatMessage(
                    {
                      id: 'guidelines.form.characterCountFooter',
                      defaultMessage: '{current}/{limit} characters',
                      description:
                        'Footer text for the guideline description input field',
                    },
                    {
                      current: sampleCharCount,
                      limit: 10000,
                    },
                  )}
                  onChange={(editor: any) => {
                    const value = editor.getData();
                    const plainText = editor.document
                      .getBody()
                      .getText()
                      .trim();
                    setSampleCharCount(plainText.length);
                    setState((prevState: Guideline) => ({
                      ...prevState,
                      sample: value as string,
                    }));
                  }}
                />

                <Layout direction="column" spacing={2}>
                  {state.variations?.map((variation) => (
                    <Variation
                      key={variation.clause_variation_id}
                      variation={variation}
                      onDelete={() => {
                        setState((prevState) => ({
                          ...prevState,
                          variations: prevState.variations?.filter(
                            (v) =>
                              v.clause_variation_id !==
                              variation.clause_variation_id,
                          ),
                        }));
                      }}
                    />
                  ))}
                </Layout>

                <Layout align="center" justify="space-between">
                  <Checkbox
                    option={{
                      label: intl.formatMessage({
                        id: 'guidelines.form.suggestRedlinesLabel',
                        defaultMessage: 'Suggest Redlines (optional)',
                        description: 'Label for suggest redlines checkbox',
                      }),
                      value: true,
                    }}
                    value={!!state.suggest_redlines}
                    onChange={(value) => {
                      setState((prevState: Guideline) => ({
                        ...prevState,
                        suggest_redlines: !!value,
                      }));
                    }}
                    name="suggestRedlines"
                  />
                </Layout>

                <Layout flex="1" mb={3}>
                  <Box flex="1">
                    <Button
                      isFullWidth
                      variant="secondary"
                      onClick={() => {
                        setClausePanelVisible(true);
                        containerRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        });
                      }}
                      icon="plus"
                      iconPosition="left"
                      text={intl.formatMessage({
                        id: 'Guideline.addExistingVariation',
                        defaultMessage: 'Add Existing Clause Variation',
                        description:
                          'Button label to add an existing variation',
                      })}
                    />
                  </Box>
                  {/* TODO uncomment once product confirms the direction of this feature */}
                  {/* <Box flex="1" ml={2}>
                    <Button
                      isFullWidth
                      variant="secondary"
                      onClick={() => {}}
                      text={intl.formatMessage({
                        id: 'Guideline.addNewVariation',
                        defaultMessage: 'Add New Clause Variation',
                        description: 'Button label to add a new variation',
                      })}
                      icon="plus"
                      iconPosition="left"
                    />
                  </Box> */}
                </Layout>
              </Layout>
            </Panel>
          </Box>

          {clausePanelVisible && (
            <Box flex="2">
              <Panel
                borderRadius="l"
                icon={{
                  icon: 'library',
                  tooltip: intl.formatMessage({
                    id: 'playbooks.Form.playbookAddClauseVariationTooltip',
                    defaultMessage: 'Library',
                    description:
                      'Tooltip for library icon in add clause variation panel',
                  }),
                }}
                title={intl.formatMessage({
                  id: 'playbooks.Form.playbookAddClauseVariation',
                  defaultMessage: 'Add Existing Clause Variation',
                  description:
                    'Title for the add clause variation section in the playbook setup form',
                })}
                actions={[
                  {
                    onClick: () => setClausePanelVisible(false),
                    icon: 'left-panel',
                    text: intl.formatMessage({
                      id: 'playbooks.Form.closeLeftPanel',
                      defaultMessage: 'Close',
                      description:
                        'Text for back to guidelines button in add clause variation panel',
                    }),
                  },
                ]}
              >
                <ImportVariation onInsertVariation={handleInserVariation} />
              </Panel>
            </Box>
          )}
        </Layout>
      </div>
    </FullPageOverlay>
  );
};
